import type { ChunkEdges, SheetGeometry } from '@/features/FormBuilder';

export type SheetAtomKind = 'node' | 'tableBand' | 'cell';

/** A box of the sheet a page cut must never cross, in px of the stage. */
export interface SheetAtom {
  top: number;
  bottom: number;
  kind: SheetAtomKind;
  tableId?: string;
  cellId?: string;
}

export interface SheetBox {
  top: number;
  bottom: number;
}

export interface SheetLayout {
  height: number;
  header: SheetBox | null;
  atoms: SheetAtom[];
  /** The text lines of the paragraphs, which a cut through a box taller than a page must not split. */
  textLines: SheetBox[];
}

export interface SheetPage {
  sourceTop: number;
  sourceHeight: number;
  targetTop: number;
  withHeader: boolean;
  /** How the page ends: `null` on the last one. */
  cut: 'clean' | 'hard' | null;
  /** The table a clean cut splits between two rows. */
  tableId?: string;
}

export const SHEET_PAGE_GEOMETRY_ERROR = 'Página da folha sem altura útil';

function crosses(atom: SheetAtom, y: number): boolean {
  return atom.top < y && y < atom.bottom;
}

// Every line the limit splits goes whole to the next page. The cut does not climb any further: two
// paragraphs side by side with offset lines would push it up to the top of the page.
function hardCutAt(textLines: SheetBox[], start: number, limit: number): number {
  const split = textLines.filter((line) => line.top < limit && limit < line.bottom);
  const cut = Math.min(limit, ...split.map((line) => line.top));
  return cut > start ? cut : limit;
}

function largestCleanCut(atoms: SheetAtom[], start: number, limit: number): number | null {
  let best: number | null = null;
  for (const atom of atoms) {
    const y = atom.bottom;
    if (y <= start || y > limit || (best !== null && y <= best)) continue;
    if (!atoms.some((other) => crosses(other, y))) best = y;
  }
  return best;
}

// A table moved whole to the next page starts with a different gap than one continued mid-way,
// as on the builder canvas.
function gapOf(kinds: SheetAtomKind[], geometry: SheetGeometry): number {
  if (kinds.includes('tableBand')) return geometry.movedTableGap;
  if (kinds.includes('cell')) return geometry.continuedTableGap;
  return 0;
}

function tableSplitAt(atoms: SheetAtom[], cut: number): string | undefined {
  const endingTables = new Set(
    atoms.filter((atom) => atom.kind === 'cell' && atom.bottom === cut).map((atom) => atom.tableId)
  );
  return atoms.find(
    (atom) => atom.kind === 'cell' && atom.top >= cut && endingTables.has(atom.tableId)
  )?.tableId;
}

export function paginateSheet(layout: SheetLayout, geometry: SheetGeometry): SheetPage[] {
  const { atoms, header, height, textLines } = layout;
  const contentEnd = atoms.reduce((end, atom) => Math.max(end, atom.bottom), 0);
  const continuedTop = (gap: number): number =>
    header
      ? geometry.pagePadding + (header.bottom - header.top) + gap
      : geometry.topMarginWithoutHeader;

  const pages: SheetPage[] = [];
  let start = 0;
  let targetTop = 0;

  for (;;) {
    const room = geometry.pageHeight - targetTop;
    const usable = room - geometry.bottomMargin;
    if (usable <= 0) throw new Error(SHEET_PAGE_GEOMETRY_ERROR);
    const withHeader = pages.length > 0 && header !== null;
    const limit = start + usable;

    if (contentEnd <= limit) {
      const end = Math.max(start, Math.min(height, start + room));
      pages.push({ sourceTop: start, sourceHeight: end - start, targetTop, withHeader, cut: null });
      return pages;
    }

    // As on the builder canvas, a box taller than a page is not pushed to the next one: it would
    // not fit there either, and the page it leaves would end half empty.
    const straddling = atoms.filter((atom) => crosses(atom, limit));
    const fitsNextPage = straddling.every((atom) => {
      const nextUsable =
        geometry.pageHeight - continuedTop(gapOf([atom.kind], geometry)) - geometry.bottomMargin;
      return atom.bottom - atom.top <= nextUsable;
    });
    const clean = fitsNextPage ? largestCleanCut(atoms, start, limit) : null;
    if (clean !== null) {
      const after = atoms.filter((atom) => atom.bottom > clean);
      const nextStart = Math.min(...after.map((atom) => atom.top));
      const nextKinds = after.filter((atom) => atom.top === nextStart).map((atom) => atom.kind);
      const tableId = tableSplitAt(atoms, clean);
      pages.push({
        sourceTop: start,
        sourceHeight: clean - start,
        targetTop,
        withHeader,
        cut: 'clean',
        ...(tableId === undefined ? {} : { tableId }),
      });
      start = nextStart;
      targetTop = continuedTop(gapOf(nextKinds, geometry));
      continue;
    }

    const hard = hardCutAt(textLines, start, limit);
    const cutKinds = atoms.filter((atom) => crosses(atom, hard)).map((atom) => atom.kind);
    pages.push({
      sourceTop: start,
      sourceHeight: hard - start,
      targetTop,
      withHeader,
      cut: 'hard',
    });
    start = hard;
    targetTop = continuedTop(cutKinds.includes('cell') ? geometry.continuedTableGap : 0);
  }
}

function addEdge(side: Record<string, string[]>, tableId: string, cellId: string): void {
  const cells = side[tableId] ?? [];
  cells.push(cellId);
  side[tableId] = cells;
}

export function edgesOf(pages: SheetPage[], layout: SheetLayout): ChunkEdges {
  const edges: ChunkEdges = { bottom: {}, top: {} };
  pages.forEach((page, index) => {
    const next = pages[index + 1];
    if (page.cut !== 'clean' || page.tableId === undefined || !next) return;
    const cut = page.sourceTop + page.sourceHeight;
    for (const atom of layout.atoms) {
      if (atom.kind !== 'cell' || atom.tableId !== page.tableId || atom.cellId === undefined)
        continue;
      if (atom.bottom === cut) addEdge(edges.bottom, page.tableId, atom.cellId);
      if (atom.top === next.sourceTop) addEdge(edges.top, page.tableId, atom.cellId);
    }
  });
  return edges;
}
